import { useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { getAutoPostingConfig, setAutoPostingConfig } from '../api/inventory';

/**
 * FI-19 — el interruptor del descuento automático, y su reflejo de solo lectura.
 *
 * Dónde vive y por qué: el CONTROL está en Conexión Loyverse, junto al «Sync automático», porque el
 * descuento se dispara justo después de cada sincronización — los dos ajustes contestan la misma
 * pregunta ("¿qué pasa solo, y cada cuánto?") y separarlos obliga a recordar en qué pantalla quedó
 * cada mitad.
 *
 * Primero lo puse en la pantalla del Descuento por ventas, con el argumento de que quien se cansa de
 * apretar el botón es quien lo quiere apagar. Falló por dos razones: quedaba dentro del bloque que
 * sólo se dibuja DESPUÉS de calcular una vista previa (o sea, había que hacer el trabajo manual para
 * encontrar cómo evitarlo), y una vez encendido esa pantalla es justo la que dejas de visitar — el
 * interruptor para apagarlo quedaba varado en la página que la automatización vuelve innecesaria.
 *
 * En el Descuento por ventas queda `AutoPostingStatus`: dice en qué estado está y lleva al control.
 * Un solo control, dos lugares donde enterarte.
 */

function useAutoPosting() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    // Quien no puede leerlo (403) simplemente no ve nada: no es un error que valga la pena mostrar.
    getAutoPostingConfig().then((config) => setEnabled(config.enabled)).catch(() => setEnabled(null));
  }, []);

  const cambiar = async (value: boolean) => {
    setSaving(true);
    setError(undefined);
    try {
      const saved = await setAutoPostingConfig({ enabled: value });
      setEnabled(saved.enabled);
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible cambiar el descuento automático.'));
    } finally {
      setSaving(false);
    }
  };

  return { enabled, saving, error, cambiar };
}

/** El control. Va en Conexión Loyverse, dentro del bloque del Dueño. */
export function AutoPostingToggle() {
  const { enabled, saving, error, cambiar } = useAutoPosting();
  if (enabled === null) return null;

  return <div className="auto-posting">
    <label className="auto-posting__row">
      <input checked={enabled} disabled={saving} onChange={(event) => void cambiar(event.target.checked)} type="checkbox" />
      <span>
        <strong>Descontar el inventario después de cada sincronización</strong>
        <span>{enabled
          ? 'Encendido. Baja del inventario los insumos de lo vendido sin que nadie apriete nada; lo que quede fuera aparece en Inicio.'
          : 'Apagado. El inventario sólo baja cuando alguien lo descuenta a mano desde Inventario → Descuento por ventas.'}</span>
      </span>
    </label>
    {error && <div className="alert alert--error">{error}</div>}
  </div>;
}

/** El reflejo. Va en Descuento por ventas: informa y lleva al control, no lo duplica. */
export function AutoPostingStatus() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    getAutoPostingConfig().then((config) => setEnabled(config.enabled)).catch(() => setEnabled(null));
  }, []);
  if (enabled === null) return null;

  return <div className="auto-posting auto-posting--status">
    <span>
      <strong>{enabled ? 'El descuento automático está encendido' : 'El descuento automático está apagado'}</strong>
      <span>{enabled
        ? 'El inventario baja solo después de cada sincronización de ventas. Aquí puedes descontar a mano de todos modos.'
        : 'Hoy el inventario sólo baja cuando lo descuentas desde aquí.'}</span>
    </span>
    <button className="text-button" onClick={() => { window.location.hash = 'integrations'; }} type="button">
      {enabled ? 'Cambiarlo en Integraciones' : 'Encenderlo en Integraciones'}
    </button>
  </div>;
}
